import { useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useOrganization } from '@clerk/clerk-react';
import http from '../helpers/requests';
import { OrganizationType } from 'modules/Common/utils/Utility';
import { useCalculateStore } from '../modules/Analytics/stores/useCalculateStore';
import { useEexAnnualPrices, resolveCarbonPrice } from 'modules/Analytics/hooks/useEexAnnualPrices';
import { getRegimeCurrency } from '../stores/useRegimeCurrencyStore';

// Prefetch interval in milliseconds (2 minutes)
const PREFETCH_INTERVAL = 2 * 60 * 1000;

// How long data is considered fresh (not stale)
export const STALE_TIME = 60 * 1000; // 1 minute

// How long data is kept in cache
export const CACHE_TIME = 5 * 60 * 1000; // 5 minutes

/**
 * Get the current year and quarter for default queries
 */
const getCurrentPeriod = () => {
  const now = new Date();
  const year = now.getFullYear();
  const quarter = `Q${Math.floor(now.getMonth() / 3) + 1}`;
  return { year, quarter };
};

/**
 * Query configurations for declarant starter pages
 */
const getDeclarantQueryConfigs = (carbonPrice, cbamFactor) => {
  const { year, quarter } = getCurrentPeriod();

  return [
    // Overview page
    {
      // Same key as useFetchDeclarantOverview: the overview reads only the period.
      queryKey: ['declarantOverview', year, quarter],
      queryFn: async () => {
        const searchParams = new URLSearchParams();
        searchParams.append('year', year);
        searchParams.append('quarter', quarter);
        const response = await http.get(`/declarant/overview?${searchParams.toString()}`);
        return response.data;
      },
      routePattern: '/overview',
    },
    // Suppliers page (first page with default filters)
    {
      queryKey: ['suppliers', '', '', '', '', '', '[]', '', '', 1, 10],
      queryFn: async () => {
        const response = await http.post('/suppliers/search', {
          page: 1,
          items: 10,
        });
        return {
          suppliers: response.data.suppliers,
          itemCount: response.data.itemCount,
          totalSuppliers: response.data.itemCount,
          completeSuppliers: response.data.infoCompleteCount,
          totalCnCodeCombinations: response.data.totalCnCodeCombinations,
          totalActualDataCombinations: response.data.totalActualDataCombinations,
        };
      },
      routePattern: '/suppliers',
    },
    // Shipments/Imports page (first page with default filters)
    {
      queryKey: ['shipmentsSearch', '', '', '', '', '', '', '', '[]', false, 1, 10],
      queryFn: async () => {
        const response = await http.post('/shipments/search', {
          ambigous: false,
          flatten: true,
          page: 1,
          items: 10,
        });
        return response.data;
      },
      routePattern: '/imports',
    },
    // Installations page (first page with default filters)
    {
      queryKey: ['installations', 1, 10],
      queryFn: async () => {
        const response = await http.post('/installations/search', {
          page: 1,
          items: 10,
        });
        return {
          installations: response.data.installations,
          itemCount: response.data.itemCount,
        };
      },
      routePattern: '/installations',
    },
    // Emissions page (first page with default filters)
    {
      queryKey: [
        'declarant-emissions',
        undefined,
        undefined,
        '[]',
        undefined,
        undefined,
        undefined,
        1,
        10,
      ],
      queryFn: async () => {
        const response = await http.post('/emissions/search/data', {
          page: 1,
          items: 10,
        });
        return response.data;
      },
      routePattern: '/emissions',
    },
    // Analytics page — not for a UK declarant: this EU-shaped request (current year,
    // EU certificate price) would only earn a 409 (uk-cbam-analytics I-3).
    ...(getRegimeCurrency() === 'GBP'
      ? []
      : [
          {
            queryKey: ['analytics', 'declarant', year, quarter, carbonPrice, cbamFactor],
            queryFn: async () => {
              const queryParams = new URLSearchParams();
              queryParams.append('year', year);
              queryParams.append('quarter', quarter);
              queryParams.append('carbonPrice', carbonPrice);
              queryParams.append('cbamFactor', cbamFactor);
              const response = await http.get(`/analytics/declarant?${queryParams.toString()}`);
              return response.data;
            },
            routePattern: '/analytics-overview',
          },
        ]),
    // Reports page. Key and payload must match useFetchReports exactly — an
    // object key (not a serialized string) and the unwrapped `res.data` — or
    // the warm entry never gets read and the page refetches anyway.
    // The prefetch mirrors the table's first paint: no filters, page 1.
    //
    // `poll: false` — warmed on sidebar hover only, not by the background
    // interval. Reports is a low-traffic page and its unfiltered request
    // otherwise races the table's own filtered ones; hover already lands the
    // request before the click.
    {
      queryKey: ['reports', { year: '', quarter: '', page: 1, limit: 10 }],
      queryFn: async () => (await http.get('/reports/?page=1&limit=10')).data,
      routePattern: '/reports',
      poll: false,
    },
    // Aggregates for the reports status bar + year pills (unfiltered).
    {
      queryKey: ['reports', 'stats'],
      queryFn: async () => (await http.get('/reports/stats')).data,
      routePattern: '/reports',
      poll: false,
    },
    // Declarant suppliers list (used by multiple components)
    {
      queryKey: ['declarant-suppliers'],
      queryFn: async () => {
        const response = await http.get('/suppliers/declarant');
        return response.data.suppliers;
      },
    },
    // Declarant installations list (used by multiple components)
    {
      queryKey: ['declarant-installations'],
      queryFn: async () => {
        const response = await http.get('/installations/declarant');
        return response.data.installations;
      },
    },
    // Countries (used by many forms)
    {
      queryKey: ['countries'],
      queryFn: async () => {
        const response = await http.get('/country/all');
        return response.data;
      },
    },
  ];
};

/**
 * Query configurations for operator starter pages
 */
const getOperatorQueryConfigs = (carbonPrice) => {
  const { year } = getCurrentPeriod();

  return [
    // Operator installations
    {
      queryKey: ['operatorInstallations'],
      queryFn: async () => {
        const response = await http.get('/operator/installations');
        return response.data;
      },
      routePattern: '/operator-installations',
    },
    // Operator analytics
    {
      // The hook's key, so the prefetch is the query the page reads. No CBAM factor: the
      // server prices the year on its own (D1).
      queryKey: ['analytics', 'operator', year, carbonPrice],
      queryFn: async () => {
        const queryParams = new URLSearchParams();
        queryParams.append('year', year);
        queryParams.append('carbonPrice', carbonPrice);
        const response = await http.get(`/analytics/operator?${queryParams.toString()}`);
        return response.data;
      },
      routePattern: '/operator-analytics',
    },
    // Countries (used by many forms)
    {
      queryKey: ['countries'],
      queryFn: async () => {
        const response = await http.get('/country/all');
        return response.data;
      },
    },
  ];
};

/**
 * Hook that pre-fetches and caches data for starter pages
 *
 * This hook:
 * 1. Pre-fetches data for key pages on mount
 * 2. Refreshes the cache periodically (every 2 minutes)
 * 3. Provides a method to prefetch specific routes on hover
 *
 * The data is still refreshed when the user navigates to the page,
 * but the cached version is shown immediately while the fresh data loads.
 */
/**
 * @param {Object} [options]
 * @param {Boolean} [options.enabled=true] — set false to suspend prefetching.
 *   AppLayout passes false on /trial-expired: every prefetch there is a
 *   guaranteed 403 for a workspace whose plan has lapsed, repeating on a 2-minute
 *   interval, and each one re-enters the trial-expiry interceptor. Harmless (the
 *   interceptor breaks the redirect loop) but pure noise, and it only started
 *   happening when that page gained the app chrome.
 */
export const usePageDataPrefetcher = ({ enabled = true } = {}) => {
  const queryClient = useQueryClient();
  const { organization } = useOrganization();
  const { carbonPrice, cbamFactor, carbonPriceHydrated, setCarbonPrice } = useCalculateStore();
  const intervalRef = useRef(null);
  const hasPrefetchedRef = useRef(false);

  const userType = organization?.publicMetadata?.type;

  // Proactively hydrate carbon price using EEX prices so that prefetching
  // has the required inputs even if no page has rendered CBAMControls yet.
  const { data: eexData, isPending: eexPending } = useEexAnnualPrices({
    year: new Date().getFullYear(),
  });

  // Hydrate once the price query has SETTLED, whatever it settled to.
  //
  // This used to hydrate only when `eexData.thisWeekPrice.price` was non-null,
  // which made `carbonPriceHydrated` a permanent false in two entirely routine
  // cases: EEX publishes weekly on a Friday (so there is no "this week" price
  // for most of a week), and the upstream call is swallowed by the backend on
  // failure. `carbonPriceHydrated` is not a nice-to-have — it hard-gates
  // `useFetchDeclarantOverview`/`useFetchOperatorOverview` (`enabled`), the
  // whole prefetcher, and the full-page skeleton branch on both overview pages.
  // Leaving it false wedged Home permanently: Next steps, Data gaps and Key
  // deadlines all sat in skeleton while every other page worked, because only
  // Home reads this flag. A market price that is legitimately absent must never
  // be able to hold the dashboard hostage — so settle-then-hydrate, with
  // `resolveCarbonPrice` supplying the fallback chain.
  useEffect(() => {
    if (carbonPriceHydrated || eexPending) return;
    setCarbonPrice(resolveCarbonPrice(eexData));
  }, [carbonPriceHydrated, eexPending, eexData, setCarbonPrice]);

  /**
   * Get the appropriate query configs based on user type
   */
  const getQueryConfigs = useCallback(() => {
    if (userType === OrganizationType.Declarant) {
      return getDeclarantQueryConfigs(carbonPrice, cbamFactor);
    } else if (userType === OrganizationType.Operator) {
      return getOperatorQueryConfigs(carbonPrice);
    }
    return [];
  }, [userType, carbonPrice, cbamFactor]);

  /**
   * Prefetch all configured queries
   */
  const prefetchAll = useCallback(async () => {
    if (!carbonPriceHydrated) {
      return;
    }

    // `poll: false` opts a config out of the background interval while leaving
    // it available to prefetchRoute (hover-intent) below — for pages where the
    // periodic traffic isn't worth it. Absent flag ⇒ polled, as before.
    const configs = getQueryConfigs().filter((config) => config.poll !== false);

    // Prefetch all queries in parallel
    const prefetchPromises = configs.map(({ queryKey, queryFn }) =>
      queryClient
        .prefetchQuery({
          queryKey,
          queryFn,
          staleTime: STALE_TIME,
        })
        // Prefetching is best-effort — swallow failures so a single bad
        // request doesn't reject the Promise.allSettled batch below.
        .catch(() => {}),
    );

    await Promise.allSettled(prefetchPromises);
  }, [queryClient, getQueryConfigs, carbonPriceHydrated]);

  /**
   * Prefetch data for a specific route (used for hover-based prefetching)
   */
  const prefetchRoute = useCallback(
    async (route) => {
      if (!carbonPriceHydrated) {
        return;
      }

      const configs = getQueryConfigs();
      const matchingConfigs = configs.filter(
        (config) => config.routePattern && route.startsWith(config.routePattern),
      );

      // Also prefetch common data like suppliers and installations lists
      const commonConfigs = configs.filter(
        (config) =>
          !config.routePattern &&
          (config.queryKey[0] === 'declarant-suppliers' ||
            config.queryKey[0] === 'declarant-installations' ||
            config.queryKey[0] === 'countries'),
      );

      const allConfigs = [...matchingConfigs, ...commonConfigs];

      const prefetchPromises = allConfigs.map(({ queryKey, queryFn }) =>
        queryClient
          .prefetchQuery({
            queryKey,
            queryFn,
            staleTime: STALE_TIME,
          })
          // Prefetching is best-effort — swallow failures so one bad
          // request doesn't reject the Promise.allSettled batch below.
          .catch(() => {}),
      );

      await Promise.allSettled(prefetchPromises);
    },
    [queryClient, getQueryConfigs, carbonPriceHydrated],
  );

  // Initial prefetch on mount and periodic refresh
  useEffect(() => {
    // Only start prefetching once we have the user type and carbon price
    if (!enabled || !userType || !carbonPriceHydrated) {
      return;
    }

    // Initial prefetch (with a small delay to not compete with initial page load)
    const initialPrefetchTimeout = setTimeout(() => {
      if (!hasPrefetchedRef.current) {
        prefetchAll();
        hasPrefetchedRef.current = true;
      }
    }, 1000);

    // Set up periodic refresh
    intervalRef.current = setInterval(() => {
      prefetchAll();
    }, PREFETCH_INTERVAL);

    return () => {
      clearTimeout(initialPrefetchTimeout);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [enabled, userType, carbonPriceHydrated, prefetchAll]);

  // Re-prefetch when carbon price or cbam factor changes
  useEffect(() => {
    if (carbonPriceHydrated && hasPrefetchedRef.current) {
      prefetchAll();
    }
  }, [carbonPrice, cbamFactor, carbonPriceHydrated, prefetchAll]);

  return {
    prefetchAll,
    prefetchRoute,
  };
};

/**
 * Hook to get the prefetch function for use in navigation components
 * This is a lightweight hook that just returns the prefetch function
 */
export const usePrefetchRoute = () => {
  const queryClient = useQueryClient();
  const { carbonPrice, cbamFactor, carbonPriceHydrated } = useCalculateStore();
  const { organization } = useOrganization();

  const userType = organization?.publicMetadata?.type;

  const prefetchRoute = useCallback(
    async (route) => {
      if (!carbonPriceHydrated || !userType) {
        return;
      }

      const configs =
        userType === OrganizationType.Declarant
          ? getDeclarantQueryConfigs(carbonPrice, cbamFactor)
          : userType === OrganizationType.Operator
            ? getOperatorQueryConfigs(carbonPrice)
            : [];

      const matchingConfigs = configs.filter(
        (config) => config.routePattern && route.startsWith(config.routePattern),
      );

      // Also prefetch common data
      const commonConfigs = configs.filter(
        (config) =>
          !config.routePattern &&
          (config.queryKey[0] === 'declarant-suppliers' ||
            config.queryKey[0] === 'declarant-installations' ||
            config.queryKey[0] === 'countries'),
      );

      const allConfigs = [...matchingConfigs, ...commonConfigs];

      await Promise.allSettled(
        allConfigs.map(({ queryKey, queryFn }) =>
          queryClient
            .prefetchQuery({
              queryKey,
              queryFn,
              staleTime: STALE_TIME,
            })
            .catch(() => {}),
        ),
      );
    },
    [queryClient, carbonPrice, cbamFactor, carbonPriceHydrated, userType],
  );

  return prefetchRoute;
};

export default usePageDataPrefetcher;
